import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock, Sparkles, Palette, X, Flame, Key } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Visual styling per style key
const STYLE_VISUALS = {
  surrealist: {
    gradient: "from-violet-400 via-purple-400 to-fuchsia-400",
    ring: "ring-violet-300/50",
    descriptionKey: "dashboard.artStyles.descriptions.surrealist",
  },
  watercolor: {
    gradient: "from-sky-300 via-cyan-300 to-teal-300",
    ring: "ring-sky-300/50",
    descriptionKey: "dashboard.artStyles.descriptions.watercolor",
  },
  oil_painting: {
    gradient: "from-amber-400 via-orange-400 to-red-400",
    ring: "ring-amber-300/50",
    descriptionKey: "dashboard.artStyles.descriptions.oil_painting",
  },
  digital_cyberpunk: {
    gradient: "from-pink-500 via-fuchsia-500 to-indigo-500",
    ring: "ring-pink-300/50",
    descriptionKey: "dashboard.artStyles.descriptions.digital_cyberpunk",
  },
  japanese_ukiyo: {
    gradient: "from-rose-300 via-pink-300 to-indigo-300",
    ring: "ring-rose-300/50",
    descriptionKey: "dashboard.artStyles.descriptions.japanese_ukiyo",
  },
  celestial: {
    gradient: "from-indigo-500 via-violet-500 to-purple-700",
    ring: "ring-indigo-300/50",
    descriptionKey: "dashboard.artStyles.descriptions.celestial",
  },
};

export default function ArtStylesShowcase({ user, recentDreamId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [styles, setStyles] = useState([]);
  const [longest, setLongest] = useState(0);
  const [isPremium, setIsPremium] = useState(false);
  const [unlockKeys, setUnlockKeys] = useState(0);
  const [modalStyle, setModalStyle] = useState(null);
  const [sampleImage, setSampleImage] = useState(null);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  const fetchStyles = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const res = await axios.get(`${API}/artwork-styles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStyles(res.data?.styles || []);
      setLongest(res.data?.user_longest_streak || 0);
      setIsPremium(!!res.data?.is_premium);
      setUnlockKeys(res.data?.unlock_keys || 0);
    } catch {
      // silently fail
    }
  };

  useEffect(() => {
    fetchStyles();
  }, []);

  // Fetch cached sample when a modal opens
  useEffect(() => {
    if (!modalStyle) {
      setSampleImage(null);
      return;
    }
    if (!modalStyle.has_sample) {
      setSampleImage(null);
      return;
    }
    let cancelled = false;
    const loadSample = async () => {
      setSampleLoading(true);
      try {
        const res = await axios.get(
          `${API}/artwork-style-samples/${modalStyle.key}`
        );
        if (!cancelled) {
          setSampleImage(
            `data:${res.data.mime_type || "image/png"};base64,${res.data.image}`
          );
        }
      } catch {
        if (!cancelled) setSampleImage(null);
      } finally {
        if (!cancelled) setSampleLoading(false);
      }
    };
    loadSample();
    return () => {
      cancelled = true;
    };
  }, [modalStyle]);

  if (!styles.length) return null;

  const unlockedCount = styles.filter((s) => s.unlocked).length;
  const total = styles.length;
  const nextLocked = !isPremium ? styles.find((s) => !s.unlocked) : null;

  const handleUseStyle = (styleKey) => {
    setModalStyle(null);
    if (recentDreamId) {
      navigate(`/dreams/${recentDreamId}?style=${styleKey}`);
    } else {
      // No dream yet — take user to journal to create one
      navigate("/journal");
    }
  };

  const handleKeepStreak = () => {
    setModalStyle(null);
    navigate("/journal");
  };

  const handleUnlockStyle = async (styleKey) => {
    setUnlocking(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/artwork-styles/${styleKey}/unlock`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        t("dashboard.artStyles.unlockedToast") || "Tyyli avattu! 🎉"
      );
      setUnlockKeys(res.data?.remaining_keys ?? Math.max(0, unlockKeys - 1));
      // Refresh styles list to update unlocked status
      await fetchStyles();
      // Update modal state so the "Use" button appears
      setModalStyle((prev) => (prev ? { ...prev, unlocked: true } : null));
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          t("dashboard.artStyles.unlockFailed") ||
          "Avaus epäonnistui"
      );
    } finally {
      setUnlocking(false);
    }
  };

  return (
    <>
      <div
        className="glass-effect rounded-3xl p-6 mb-8"
        data-testid="art-styles-showcase"
      >
        <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center shadow-md">
              <Palette className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-heading text-xl font-light text-void">
                {t("dashboard.artStyles.title") || "AI-taidetyylien keräily"}
              </h3>
              <p className="text-sm text-void/60 font-body">
                {isPremium
                  ? t("dashboard.artStyles.premiumSubtitle") ||
                    "Premium — kaikki tyylit avoinna"
                  : t("dashboard.artStyles.subtitle", {
                      unlocked: unlockedCount,
                      total,
                    }) || `${unlockedCount} / ${total} tyyliä avattu`}
              </p>
            </div>
          </div>

          {!isPremium && unlockKeys > 0 && (
            <div
              className="flex items-center gap-2 text-xs font-body bg-gradient-to-r from-amber-100 to-yellow-100 border border-amber-300/50 text-amber-800 rounded-full px-3 py-1.5 shadow-sm"
              data-testid="unlock-keys-badge"
            >
              <Key className="w-3.5 h-3.5" />
              <span className="font-medium">
                {t("dashboard.artStyles.keysAvailable", { count: unlockKeys }) ||
                  `${unlockKeys} avain${unlockKeys === 1 ? "" : "ta"}`}
              </span>
            </div>
          )}

          {!isPremium && nextLocked && unlockKeys === 0 && (
            <div
              className="text-xs font-body text-void/60 bg-white/50 rounded-full px-3 py-1.5"
              data-testid="next-style-unlock"
            >
              ✨ {t("dashboard.artStyles.nextKeyHint") ||
                "Seuraava avain 7 päivän streakistä"}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {styles.map((s) => {
            const visual = STYLE_VISUALS[s.key] || STYLE_VISUALS.surrealist;
            const unlocked = s.unlocked;
            return (
              <button
                key={s.key}
                onClick={() => setModalStyle(s)}
                className={`relative rounded-2xl overflow-hidden aspect-square transition-all duration-500 cursor-pointer text-left ${
                  unlocked
                    ? `ring-2 ${visual.ring} shadow-md hover:-translate-y-1 hover:shadow-xl hover:scale-[1.02]`
                    : "opacity-60 grayscale hover:opacity-80 hover:grayscale-0"
                }`}
                data-testid={`showcase-style-${s.key}`}
                aria-label={`${s.label} — ${unlocked ? "unlocked" : "locked"}`}
              >
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${visual.gradient}`}
                />
                <div className="absolute inset-0 bg-black/10" />
                {unlocked && (
                  <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/20 to-white/0" />
                )}
                <div className="absolute inset-0 flex flex-col items-center justify-center p-2 text-center">
                  {unlocked ? (
                    <Sparkles className="w-5 h-5 text-white/90 mb-1.5" />
                  ) : (
                    <Lock className="w-5 h-5 text-white/80 mb-1.5" />
                  )}
                  <span className="text-[11px] sm:text-xs font-body font-medium text-white leading-tight">
                    {s.label}
                  </span>
                  {!unlocked && !isPremium && unlockKeys > 0 && (
                    <span className="text-[10px] text-amber-200 font-body mt-1 flex items-center gap-1">
                      <Key className="w-3 h-3" />
                      {t("dashboard.artStyles.spendKey") || "Käytä avain"}
                    </span>
                  )}
                  {!unlocked && !isPremium && unlockKeys === 0 && (
                    <span className="text-[10px] text-white/80 font-body mt-1">
                      {t("dashboard.artStyles.lockedNeedKey") || "Tarvitset avaimen"}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {!isPremium && unlockedCount < total && (
          <p className="text-[11px] text-void/50 font-body italic mt-4 text-center">
            {t("dashboard.artStyles.hint") ||
              "Pidä streak-sarjaa yllä avataksesi uusia AI-taidetyylejä unitaiteellesi."}
          </p>
        )}
      </div>

      {/* Style Detail Modal */}
      {modalStyle && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setModalStyle(null)}
          data-testid="art-style-modal"
        >
          <div
            className="relative w-full max-w-md bg-white rounded-3xl overflow-hidden shadow-2xl animate-in zoom-in-95 fade-in duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header: gradient preview with optional real sample image */}
            <div
              className={`relative h-56 bg-gradient-to-br ${
                (STYLE_VISUALS[modalStyle.key] || STYLE_VISUALS.surrealist)
                  .gradient
              } ${modalStyle.unlocked ? "" : "grayscale"}`}
            >
              {sampleImage && (
                <img
                  src={sampleImage}
                  alt={modalStyle.label}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                    modalStyle.unlocked ? "" : "grayscale"
                  }`}
                  data-testid="art-style-sample-image"
                />
              )}
              {sampleLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                  <div className="w-8 h-8 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
              <button
                onClick={() => setModalStyle(null)}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-colors z-10"
                aria-label="Close"
                data-testid="art-style-modal-close"
              >
                <X className="w-4 h-4 text-void" />
              </button>
              <div className="absolute inset-x-0 bottom-0 flex flex-col items-center text-center px-6 pb-4">
                {modalStyle.unlocked ? (
                  <Sparkles className="w-7 h-7 text-white/95 mb-1.5 drop-shadow" />
                ) : (
                  <Lock className="w-7 h-7 text-white/90 mb-1.5 drop-shadow" />
                )}
                <h3 className="text-2xl font-heading font-light text-white drop-shadow">
                  {modalStyle.label}
                </h3>
              </div>
            </div>

            {/* Body */}
            <div className="p-6">
              <p className="text-sm text-void/70 font-body leading-relaxed mb-5">
                {t(
                  `dashboard.artStyles.descriptions.${modalStyle.key}`,
                  {
                    defaultValue:
                      t("dashboard.artStyles.descriptions.surrealist") || "",
                  }
                )}
              </p>

              {modalStyle.unlocked ? (
                <Button
                  onClick={() => handleUseStyle(modalStyle.key)}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-full font-body font-medium h-11"
                  data-testid="art-style-use-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {recentDreamId
                    ? t("dashboard.artStyles.useWithDream") ||
                      "Käytä viimeisimpään uneen"
                    : t("dashboard.artStyles.writeFirstDream") ||
                      "Kirjoita uni käyttääksesi"}
                </Button>
              ) : (
                <div className="space-y-3">
                  {unlockKeys > 0 ? (
                    <>
                      <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-300 p-4">
                        <p className="text-sm font-body text-void/80 flex items-start gap-2">
                          <Key className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                          <span>
                            {t("dashboard.artStyles.unlockWithKeyInfo", { count: unlockKeys }) ||
                              `Sinulla on ${unlockKeys} avain${unlockKeys === 1 ? "" : "ta"}. Käytä yksi avataksesi tämän tyylin pysyvästi.`}
                          </span>
                        </p>
                      </div>
                      <Button
                        onClick={() => handleUnlockStyle(modalStyle.key)}
                        disabled={unlocking}
                        className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white rounded-full font-body font-medium h-11 shadow-md"
                        data-testid="art-style-unlock-btn"
                      >
                        <Key className="w-4 h-4 mr-2" />
                        {unlocking
                          ? (t("dashboard.artStyles.unlocking") || "Avataan...")
                          : (t("dashboard.artStyles.spendKeyToUnlock") || "Käytä avain ja avaa")}
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4">
                        <p className="text-sm font-body text-void/80 flex items-start gap-2">
                          <Lock className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                          <span>
                            {t("dashboard.artStyles.needKey") ||
                              "Tarvitset avaimen avataksesi tämän tyylin. Saat yhden avaimen jokaisesta 7 päivän streak-virstanpylväästä."}
                          </span>
                        </p>
                      </div>
                      <Button
                        onClick={handleKeepStreak}
                        className="w-full bg-gradient-to-r from-orange-400 to-red-500 hover:opacity-90 text-white rounded-full font-body font-medium h-11"
                        data-testid="art-style-keep-streak-btn"
                      >
                        <Flame className="w-4 h-4 mr-2" />
                        {t("dashboard.artStyles.keepStreakGoing") ||
                          "Jatka streak-sarjaa"}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
